import { t } from '@web/i18n';
import { AppIcons } from '@web/ui/core';

export interface TriagePillProps {
  /** `null` while loading — the pill stays hidden, same as `0`. */
  total: number | null;
  onClick?: () => void;
}

/**
 * Triage pill — small inline chip shown on the overview header when
 * there are cards without a squad awaiting human routing. Mirrors the
 * `.tri-pill` spec from the mockup: leading red dot, tray icon, body
 * text, divider + mono CTA. The tap target navigates to the triage
 * inbox; the handler is wired by the page.
 */
export function TriagePill({
  total,
  onClick,
}: Readonly<TriagePillProps>): React.JSX.Element | null {
  if (total === null || total === 0) return null;

  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2.5 self-start rounded-lg border border-tone-red-fg/25 bg-tone-red-tint/60 py-2 pl-2.5 pr-3 text-left transition-colors duration-base ease-standard hover:bg-tone-red-tint"
    >
      <span aria-hidden className="h-2 w-2 flex-shrink-0 rounded-pill bg-tone-red-fg" />
      <AppIcons name="tray" size={14} className="text-tone-red-ink/70" />
      <span className="text-meta text-foreground">
        <strong className="font-medium">{total}</strong>{' '}
        {t('overview.triage.body', { count: total })}
      </span>
      <span className="ml-1.5 inline-flex items-center gap-1 border-l border-tone-red-fg/25 pl-2.5 font-mono text-tiny uppercase tracking-eyebrow text-tone-red-ink/70">
        <span>{t('overview.triage.cta')}</span>
        <AppIcons name="arrow-right" size={12} />
      </span>
    </button>
  );
}
